/**
 * What a widget says while a call connects, per start stage.
 *
 * Shared by the chat flag (its placeholder line) and the side flag (its pill),
 * so the two flags narrate the same four legs in the same words — they are one
 * product with two behaviours, and a caller who has seen one must recognise the
 * other. Narration, not diagnostics: the point is that the line CHANGES while
 * the caller waits (a static "Connecting…" over four seconds reads as stuck),
 * and that each label names something a person can believe takes a moment.
 * "Security context" is the honest star — that stage really is the customer's
 * own identity webhook.
 *
 * Its own module rather than a corner of `types.ts`, which deliberately imports
 * nothing and carries no copy; and not either flag's file, because the side
 * flag importing from the 2,500-line chat flag would point the dependency the
 * wrong way — the chat flag is the port OF the side flag.
 */
import type { VoicePhase, VoiceStartStage } from './types'

export const STAGE_TEXT: Record<VoiceStartStage, string> = {
  checking: 'Checking your browser…',
  security: 'Getting your security context…',
  audio: 'Connecting audio…',
  joining: 'Joining the conversation…',
}

/**
 * The connecting line for a control that may or may not report a stage. A
 * hand-made control in a test, a state preview, or an older adapter reports
 * none and gets the widget's own plain word instead.
 */
export function connectingText(stage: VoiceStartStage | null | undefined, fallback: string): string {
  return stage === null || stage === undefined ? fallback : STAGE_TEXT[stage]
}

/**
 * What a widget says once a person in the panel has taken the call over from
 * the AI. The caller's page keeps working exactly as before — same button, same
 * mute, same hang-up — so the only thing that must change is the words: the
 * voice they now hear is a person's, and they should not have to guess.
 */
export const TAKEN_OVER_TEXT = 'You’re now speaking with a person.'

/**
 * `text`, or the takeover line while the call is live and a person holds it.
 * One helper for every widget's status line, so they all say the same thing.
 */
export function withTakeover(control: { phase: VoicePhase; takenOver?: boolean }, text: string): string {
  const live = control.phase === 'listening' || control.phase === 'thinking' || control.phase === 'speaking'
  return live && control.takenOver === true ? TAKEN_OVER_TEXT : text
}
